import React, { useState } from 'react';
import { useStore } from 'context/StoreContext';
import { useAuth } from 'context/AuthContext';
import { Card, CardHeader } from 'components/common/Card';
import { Button } from 'components/common/Button';
import { Badge } from 'components/common/Badge';
import { Dialog } from 'components/common/Dialog';
import { Pencil, Settings, Users } from 'lucide-react';
import { Pagination } from 'components/common/Pagination';
import { useNavigate } from 'react-router-dom';
import { CreateStoreInput, OrganizationService } from 'services/org/organization.service';

const emptyStore: CreateStoreInput = { name: '', store_number: '', address: '', city: '', state: '', zip: '', timezone: 'America/New_York', phone: '' };

export default function SettingsPage() {
  const { org, stores, refresh } = useStore();
  const [storesPage, setStoresPage] = useState(1);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [addStoreOpen, setAddStoreOpen] = useState(false);
  const [editingStoreId, setEditingStoreId] = useState<string | null>(null);
  const [storeForm, setStoreForm] = useState<CreateStoreInput>(emptyStore);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const updateStoreForm = (field: keyof CreateStoreInput, value: string) => setStoreForm(current => ({ ...current, [field]: value }));

  const addStore = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!org || !storeForm.name.trim()) return;
    setSubmitting(true); setError('');
    try {
      await OrganizationService.createStore(org.id, { ...storeForm, name: storeForm.name.trim() });
      await refresh();
      setAddStoreOpen(false);
      setStoreForm(emptyStore);
    } catch (storeError: any) {
      setError(storeError?.message || 'Unable to add store.');
    } finally { setSubmitting(false); }
  };

  const editStore = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!org || !editingStoreId || !storeForm.name.trim()) return;
    setSubmitting(true); setError('');
    try {
      await OrganizationService.updateStore(org.id, editingStoreId, { ...storeForm, name: storeForm.name.trim() });
      await refresh();
      setEditingStoreId(null);
      setStoreForm(emptyStore);
    } catch (storeError: any) {
      setError(storeError?.message || 'Unable to update store.');
    } finally { setSubmitting(false); }
  };

  const openEditStore = (store: typeof stores[number]) => {
    setError('');
    setStoreForm({ name: store.name, store_number: store.store_number || '', address: store.address || '', city: store.city || '', state: store.state || '', zip: store.zip || '', timezone: store.timezone || 'America/New_York', phone: store.phone || '' });
    setEditingStoreId(store.id);
  };

  const canEditStores = (org?.role ?? user?.role) === 'owner';
  const canManageUsers = ['owner', 'manager'].includes(org?.role ?? user?.role ?? '');

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="flex items-center gap-2">
        <Settings size={20} className="text-gray-600" />
        <h1 className="text-xl font-bold text-gray-900">Settings</h1>
      </div>

      <Card>
        <CardHeader title="Organization" />
        <div className="space-y-3 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Name</span><span className="font-medium">{org?.name}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Plan</span>
            <Badge variant="success">{org?.subscription_status?.toUpperCase()}</Badge>
          </div>
          <div className="flex justify-between"><span className="text-gray-500">Target margin</span><span className="font-medium">{(org?.target_margin ?? 0.30) * 100}%</span></div>
        </div>
      </Card>

      <Card>
        <CardHeader title="Profile" />
        <div className="space-y-3 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Name</span><span className="font-medium">{user?.full_name}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="font-medium">{user?.email}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Role</span><Badge variant="info">{user?.role?.toUpperCase()}</Badge></div>
        </div>
      </Card>

      <Card>
        <CardHeader title="Users & access" subtitle="Invite employees and control their store access." />
        {canManageUsers && <Button variant="secondary" size="sm" onClick={() => navigate('/settings/users')}>
          <Users size={14} /> Manage users
        </Button>}
      </Card>

      <Card>
        <CardHeader title="Stores" />
        <div className="space-y-2">
          {stores.slice((storesPage - 1) * 8, storesPage * 8).map(s => (
            <div key={s.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
              <div>
                <p className="text-sm font-medium text-gray-900">{s.name}</p>
                <p className="text-xs text-gray-400">{s.city}, {s.state} · {s.phone}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={s.status === 'active' ? 'success' : 'default'}>{s.status}</Badge>
                {canEditStores && <Button aria-label={`Edit ${s.name}`} title={`Edit ${s.name}`} variant="ghost" size="sm" onClick={() => openEditStore(s)}><Pencil size={14} /></Button>}
              </div>
            </div>
          ))}
        </div>
        <Pagination page={storesPage} pageSize={8} total={stores.length} onPageChange={setStoresPage} />
        {canEditStores && <Button variant="secondary" size="sm" className="mt-3" onClick={() => { setError(''); setAddStoreOpen(true); }}>+ Add Store</Button>}
      </Card>

      {canEditStores && <Card>
        <CardHeader title="Billing" subtitle="Manage your subscription" />
        <Button variant="secondary" size="sm" onClick={() => navigate('/settings/billing')}>Manage Billing</Button>
      </Card>}

      <Dialog open={addStoreOpen} title="Add a store" onClose={() => { setError(''); setAddStoreOpen(false); }}>
        <StoreForm form={storeForm} error={error} submitting={submitting} onChange={updateStoreForm} onSubmit={addStore} onCancel={() => { setError(''); setAddStoreOpen(false); }} submitLabel="Add store" />
      </Dialog>

      <Dialog open={!!editingStoreId} title="Edit store" onClose={() => { setError(''); setEditingStoreId(null); }}>
        <StoreForm form={storeForm} error={error} submitting={submitting} onChange={updateStoreForm} onSubmit={editStore} onCancel={() => { setError(''); setEditingStoreId(null); }} submitLabel="Save changes" />
      </Dialog>
    </div>
  );
}

function StoreForm({ form, error, submitting, onChange, onSubmit, onCancel, submitLabel }: { form: CreateStoreInput; error: string; submitting: boolean; onChange: (field: keyof CreateStoreInput, value: string) => void; onSubmit: (event: React.FormEvent) => void; onCancel: () => void; submitLabel: string }) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
          {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Store name</label>
            <input required autoFocus value={form.name} onChange={event => onChange('name', event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-sm font-medium text-gray-700">Store number</label><input value={form.store_number} onChange={event => onChange('store_number', event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" /></div>
            <div><label className="mb-1 block text-sm font-medium text-gray-700">Phone</label><input type="tel" value={form.phone} onChange={event => onChange('phone', event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" /></div>
          </div>
          <div><label className="mb-1 block text-sm font-medium text-gray-700">Address</label><input value={form.address} onChange={event => onChange('address', event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-sm font-medium text-gray-700">City</label><input value={form.city} onChange={event => onChange('city', event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" /></div>
            <div><label className="mb-1 block text-sm font-medium text-gray-700">State</label><input maxLength={2} value={form.state} onChange={event => onChange('state', event.target.value.toUpperCase())} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-sm font-medium text-gray-700">ZIP code</label><input value={form.zip} onChange={event => onChange('zip', event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" /></div>
            <div><label className="mb-1 block text-sm font-medium text-gray-700">Timezone</label><input value={form.timezone} onChange={event => onChange('timezone', event.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2"><Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button><Button type="submit" loading={submitting}>{submitLabel}</Button></div>
    </form>
  );
}

